import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";

function SectionLabel({ n, children }: { n: string; children: React.ReactNode }) {
  return (
    <p className="mb-1.5 font-mono text-[14px] uppercase tracking-[0.14em] text-brand-accent">
      {n} {children}
    </p>
  );
}

function H2({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-3 font-serif text-[22px] tracking-[-0.01em] text-brand-ink lg:text-[26px]">
      {children}
    </h2>
  );
}

function Section({
  id, n, label, title, children,
}: {
  id: string; n: string; label: string; title: string; children: React.ReactNode;
}) {
  return (
    <section id={id} className="border-t border-brand-line px-[22px] py-9 lg:px-10 lg:py-12">
      <SectionLabel n={n}>{label}</SectionLabel>
      <H2>{title}</H2>
      <div className="space-y-3 text-[14px] leading-[1.6] text-brand-ink-2 lg:text-[13.5px]">
        {children}
      </div>
    </section>
  );
}

export function SampleIRRDocument() {
  const today = new Date().toLocaleDateString("en-US", {
    year: "numeric", month: "long", day: "numeric",
  });
  return (
    <>
      {/* Title block */}
      <header className="px-[22px] pt-9 pb-6 lg:px-10 lg:pt-12">
        <p className="mb-2 font-mono text-[14px] uppercase tracking-[0.14em] text-brand-accent">
          Sample · fictional subject
        </p>
        <h1 className="font-serif text-[28px] tracking-[-0.015em] text-brand-ink lg:text-[36px]">
          CapitalDart Investor Readiness Report (IRR)
        </h1>
        <p className="mt-3 font-serif italic text-brand-accent text-[18px] lg:text-[22px]">
          Solstice BioTherapeutics · $30M Series C
        </p>
        <p className="mt-3 max-w-[640px] text-[14px] leading-[1.55] text-brand-ink-2 lg:text-[15px]">
          A practical review of what investors will like, what they will
          challenge, and what to fix before outreach.
        </p>
        <p className="mt-3 text-[11.5px] text-brand-muted">
          Subject: Solstice BioTherapeutics · Version 1 · {today}
        </p>

        <div className="mt-6 grid grid-cols-1 gap-3 lg:grid-cols-2">
          <div className="rounded-md border border-brand-line bg-brand-paper-2 p-4">
            <div className="font-mono text-[14px] uppercase tracking-[0.12em] text-brand-muted">
              Investor Readiness Score
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="font-serif text-[44px] leading-none text-brand-ink tabular-nums">72</span>
              <span className="text-[14px] text-brand-muted">/ 100</span>
            </div>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-brand-line">
              <div className="h-full rounded-full bg-brand-accent" style={{ width: "72%" }} />
            </div>
            <div className="mt-3 inline-flex rounded-full border border-brand-accent/40 bg-brand-accent/10 px-2.5 py-0.5 text-[11px] font-medium text-brand-accent">
              Proceed with selective edits
            </div>
          </div>
          <div className="rounded-md border border-brand-line bg-brand-paper-2 p-4">
            <div className="font-mono text-[14px] uppercase tracking-[0.12em] text-brand-muted">
              Source Quality
            </div>
            <dl className="mt-2 space-y-1 text-[12.5px] text-brand-ink-2">
              <div className="flex justify-between"><dt>Extraction quality</dt><dd className="text-emerald-700 font-medium">good</dd></div>
              <div className="flex justify-between"><dt>Slides reviewed</dt><dd className="font-mono tabular-nums">18</dd></div>
              <div className="flex justify-between"><dt>Slides excluded</dt><dd className="font-mono tabular-nums">0</dd></div>
            </dl>
          </div>
        </div>
      </header>

      <Section id="exec" n="01" label="Executive Summary" title="Executive Summary">
        <p>
          Solstice BioTherapeutics is raising a $30M Series C to advance two clinical-stage gene therapy assets through Phase 2 readouts in the next eighteen months. The lead has committed $12M and the round is structured with $18M remaining at a $145M pre-money. The science is credible, the IP position is defensible, and the management team has prior FDA approval experience.
        </p>
        <p>
          The pitch under-sells late-stage manufacturing readiness and over-indexes on platform narrative relative to near-term clinical catalysts &mdash; both fixable before outreach. Recommend proceeding with the edits flagged in Section 05 before approaching tier-one healthcare investors.
        </p>
      </Section>

      <Section id="hook" n="02" label="Story Hook" title="Story Hook">
        <p className="rounded-md border-l-2 border-brand-accent bg-brand-paper-2 px-4 py-3 font-serif text-[15px] italic text-brand-ink">
          &ldquo;Two Phase 2 assets, one validated delivery platform, twelve months to the first registrational readout. The next eighteen months turn Solstice from a platform story into a clinical-stage company with cards on the table.&rdquo;
        </p>
        <p>
          The hook works because it grounds a platform pitch in concrete near-term events. Most growth-stage healthcare investors discount platform language by default; pairing it with a dated catalyst restores credibility. Lead with this on the first call and the first follow-up email.
        </p>
      </Section>

      <Section id="highlights" n="03" label="Top Highlights" title="Top Highlights">
        <ul className="space-y-2">
          {[
            "Two clinical assets, both in Phase 2, with the lead asset (SLST-201) on track for an interim readout in Q1 2027.",
            "Lead investor has committed $12M and signed the term sheet at $145M pre-money. Round is not speculative.",
            "Management team includes two prior FDA approvals in adjacent therapeutic areas and a CMO recruited from a top-three commercial-stage gene therapy company.",
            "IP estate covers composition-of-matter on the delivery vector through 2041, with continuation strategy filed.",
            "Defined commercial runway: 24 months of cash at close, covering both interim readouts plus a planned Phase 3 design alignment meeting.",
          ].map((t, i) => (
            <li key={i} className="flex gap-2.5">
              <span className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-brand-accent" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section id="risks" n="04" label="Risks & Mitigants" title="Risks &amp; Mitigants">
        <div className="overflow-x-auto rounded-md border border-brand-line">
          <table className="w-full min-w-[560px] text-left text-[12.5px]">
            <thead className="bg-brand-paper-2 text-[14px] uppercase tracking-[0.08em] text-brand-muted">
              <tr>
                <th className="px-3 py-2 font-medium">Risk</th>
                <th className="px-3 py-2 font-medium w-24">Severity</th>
                <th className="px-3 py-2 font-medium">Mitigant</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-line bg-brand-paper text-brand-ink-2">
              {[
                {
                  risk: "CMC and manufacturing scale-up timeline not addressed in the deck. Most growth-stage healthcare investors will flag this within the first meeting.",
                  sev: "Medium",
                  mit: "Add a one-page CMC slide covering current GMP partner, batch size, and the path to commercial supply. Cite the existing Tier 1 CMO partnership and the Q3 tech-transfer milestone already in the data room.",
                },
                {
                  risk: "Competitive positioning vs. two well-funded gene therapy platforms targeting overlapping indications is missing.",
                  sev: "Medium",
                  mit: "Add a one-slide competitive landscape with three named comps and the two differentiators (delivery selectivity, dosing interval). Investors will ask in meeting two regardless.",
                },
                {
                  risk: "Phase 2 trial enrollment timeline appears aggressive relative to disclosed site activations.",
                  sev: "Low",
                  mit: "Re-state enrollment as a range (Q3-Q4 2026) and disclose the contingency site activation already in motion. Honest framing builds credibility on subsequent timelines.",
                },
                {
                  risk: "Reimbursement and payer strategy mentioned only in the appendix; growth-stage investors expect a defined view by Series C.",
                  sev: "Low",
                  mit: "Pull the payer slide forward into the main body and add the named payer advisor relationship. Two paragraphs is sufficient at this stage.",
                },
              ].map((r, i) => (
                <tr key={i}>
                  <td className="px-3 py-2.5 align-top">{r.risk}</td>
                  <td className="px-3 py-2.5 align-top">
                    <span className={
                      "rounded-full border px-2 py-0.5 text-[14px] font-medium " +
                      (r.sev === "Medium"
                        ? "border-amber-500/40 bg-amber-500/10 text-amber-700"
                        : "border-emerald-500/40 bg-emerald-500/10 text-emerald-700")
                    }>
                      {r.sev}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 align-top">{r.mit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section id="changes" n="05" label="Recommended Changes" title="Recommended Changes">
        <ol className="space-y-3">
          {[
            {
              t: "Move the clinical catalyst timeline forward.",
              d: "Make slide 4 a dated catalyst calendar: SLST-201 interim Q1 2027, SLST-204 readout Q3 2027, FDA Type B Q1 2027. Investors buy events, not platforms.",
            },
            {
              t: "Add a CMC and manufacturing slide.",
              d: "One page. Current GMP partner, current batch size, commercial supply plan. Reference the Q3 tech-transfer milestone. Resolves the most common Series C objection without lengthening the deck.",
            },
            {
              t: "Tighten the platform narrative.",
              d: "Cut slides 8-11 from four pages to two. Keep the delivery selectivity data and the dosing interval comparison; cut the mechanism walkthrough. The science holds; the explanation can move to the data room.",
            },
            {
              t: "Add named competitive landscape.",
              d: "Three comps, named, with one row each on stage, dosing, and known efficacy. Honest comparison reads as strength, not weakness, at this stage.",
            },
            {
              t: "Rewrite the use of proceeds.",
              d: "Replace the pie chart with a two-column table tying each $5M increment to a specific clinical milestone. Growth-stage investors want milestones, not categories.",
            },
            {
              t: "Promote the payer strategy from appendix to body.",
              d: "Two-paragraph framing on the main commercial slide. Mention the payer advisor relationship by name and the planned market-access milestone.",
            },
          ].map((c, i) => (
            <li key={i} className="flex gap-3">
              <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-ink font-mono text-[11px] font-semibold text-brand-paper tabular-nums">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div>
                <div className="font-semibold text-brand-ink">{c.t}</div>
                <p className="mt-0.5">{c.d}</p>
              </div>
            </li>
          ))}
        </ol>
      </Section>

      <Section id="slides" n="06" label="Slide-by-Slide Deck Comments" title="Slide-by-Slide Deck Comments">
        <div className="overflow-x-auto rounded-md border border-brand-line">
          <table className="w-full min-w-[560px] text-left text-[12.5px]">
            <thead className="bg-brand-paper-2 text-[14px] uppercase tracking-[0.08em] text-brand-muted">
              <tr>
                <th className="px-3 py-2 font-medium w-14">Slide</th>
                <th className="px-3 py-2 font-medium">Title</th>
                <th className="px-3 py-2 font-medium">Reviewer note</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-line bg-brand-paper text-brand-ink-2">
              {[
                { n: 1, t: "Cover", note: "Tagline reads as platform-first. Reframe to lead with the Q1 2027 interim readout." },
                { n: 4, t: "Pipeline overview", note: "Strong. Add Phase status pills inline; visual hierarchy is hard to scan in the current layout." },
                { n: 7, t: "SLST-201 mechanism", note: "Detail-dense. Move two of the three mechanism panels to the appendix; keep the efficacy data." },
                { n: 10, t: "Competitive landscape", note: "Currently a logo wall. Replace with a three-row comp table covering stage, dosing, and disclosed efficacy." },
                { n: 13, t: "Use of proceeds", note: "Pie chart is generic. Replace with a milestone-tied table mapping each $5M increment to a specific deliverable." },
                { n: 16, t: "Team", note: "Good content, weak framing. Lead with the two prior FDA approvals; current order buries the credential." },
              ].map((r) => (
                <tr key={r.n}>
                  <td className="px-3 py-2.5 align-top font-mono tabular-nums text-brand-ink">{r.n}</td>
                  <td className="px-3 py-2.5 align-top text-brand-ink">{r.t}</td>
                  <td className="px-3 py-2.5 align-top">{r.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11.5px] italic text-brand-muted">
          Showing 6 of 16 slides reviewed; full annotations in your IRR.
        </p>
      </Section>

      <Section id="qa" n="07" label="Investor Q&A Prep" title="Investor Q&amp;A Prep">
        {[
          {
            q: "What is your regulatory path and what is the next inflection point?",
            a: "Both lead assets are in Phase 2 under existing INDs. SLST-201 has an interim readout expected Q1 2027 with a primary endpoint of >40% reduction in target biomarker at 12 weeks. SLST-204 reads out Q3 2027. We have pre-aligned the Phase 3 design with FDA at a Type B meeting in March, so a positive Phase 2 supports a registrational study without a second pre-Phase-3 meeting.",
          },
          {
            q: "How does your delivery platform differentiate from competing gene therapy approaches?",
            a: "Two specifics. First, tissue selectivity: our delivery vector demonstrates 8-12x preferential uptake in target tissue versus liver, measured across three preclinical models. Second, dosing interval: a single-administration regimen versus the quarterly re-dosing standard in the comparable class. Both are reproducible across the two indications in the current pipeline.",
          },
          {
            q: "What does your manufacturing path look like at commercial scale?",
            a: "We are running clinical supply through our Tier 1 CMO partner at 200L batch size with successful production runs through Q2. Tech transfer to commercial-scale manufacturing is scheduled Q3 with the same partner. Commercial supply contract terms are agreed in principle and convert at first commercial sale.",
          },
          {
            q: "What is your use of proceeds and runway?",
            a: "The $30M provides 24 months of runway, covering both Phase 2 interim readouts, the Type B meeting alignment, and initial Phase 3 site activation. Approximately $18M funds clinical operations and CMC, $7M funds discovery and platform extension, and $5M covers G&A and reserve.",
          },
          {
            q: "Who is on your board and what governance changes come with this round?",
            a: "Current board is five seats: two founders, two investors, one independent. This round adds one investor seat for the lead and one independent with commercial-stage gene therapy experience already identified. We are happy to walk through the slate.",
          },
        ].map((p, i) => (
          <div key={i} className="rounded-md border border-brand-line bg-brand-paper-2 p-4">
            <p className="font-serif text-[14px] italic text-brand-ink">Q. {p.q}</p>
            <p className="mt-1.5 text-[12.5px] leading-[1.6] text-brand-ink-2">A. {p.a}</p>
          </div>
        ))}
      </Section>

      <Section id="dataroom" n="08" label="Data Room Checklist" title="Data Room Checklist">
        <ul className="space-y-1.5">
          {[
            { t: "Pitch deck (current version)", s: "ready" },
            { t: "Cap table, fully diluted", s: "ready" },
            { t: "Term sheet (signed by lead)", s: "ready" },
            { t: "Audited financials (last 2 years)", s: "ready" },
            { t: "IP estate summary and filings", s: "ready" },
            { t: "Preclinical and Phase 2 interim data package", s: "ready" },
            { t: "FDA correspondence and meeting minutes", s: "ready" },
            { t: "CMC and manufacturing plan", s: "needs update" },
            { t: "Competitive landscape brief", s: "missing" },
            { t: "Reimbursement / market access memo", s: "needs update" },
            { t: "Use of proceeds, milestone-tied", s: "missing" },
            { t: "Team bios and references", s: "ready" },
          ].map((row, i) => {
            const cls = row.s === "ready"
              ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700"
              : row.s === "needs update"
              ? "border-amber-500/40 bg-amber-500/10 text-amber-700"
              : "border-red-500/40 bg-red-500/10 text-red-700";
            return (
              <li key={i} className="flex items-center justify-between gap-3 rounded-md border border-brand-line bg-brand-paper px-3 py-2">
                <span className="text-[12.5px] text-brand-ink-2">{row.t}</span>
                <span className={"shrink-0 rounded-full border px-2 py-0.5 text-[14px] font-medium " + cls}>
                  {row.s}
                </span>
              </li>
            );
          })}
        </ul>
      </Section>

      <Section id="comps" n="09" label="Comparable Transactions" title="Comparable Transactions">
        <p>
          Recent Phase 2 gene therapy Series C raises in adjacent or overlapping therapeutic profiles, calibrating expectations on size, lead, and pre-money:
        </p>
        <div className="overflow-x-auto rounded-md border border-brand-line">
          <table className="w-full min-w-[560px] text-left text-[12.5px]">
            <thead className="bg-brand-paper-2 text-[14px] uppercase tracking-[0.08em] text-brand-muted">
              <tr>
                <th className="px-3 py-2 font-medium">Company</th>
                <th className="px-3 py-2 font-medium">Raise</th>
                <th className="px-3 py-2 font-medium">Lead</th>
                <th className="px-3 py-2 font-medium">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-line bg-brand-paper text-brand-ink-2">
              {[
                { c: "Aurora Bio", r: "$45M", l: "Stonecreek Capital Partners", n: "Single-indication, Phase 2 interim readout pending." },
                { c: "Helios Cell Therapy", r: "$38M", l: "Polaris Growth Equity", n: "Two-asset platform, similar dosing profile." },
                { c: "Vermillion Therapeutics", r: "$52M", l: "Beacon Hill Ventures", n: "Larger raise; broader pipeline, three Phase 2 assets." },
              ].map((row) => (
                <tr key={row.c}>
                  <td className="px-3 py-2.5 font-medium text-brand-ink">{row.c}</td>
                  <td className="px-3 py-2.5 font-mono tabular-nums">{row.r}</td>
                  <td className="px-3 py-2.5">{row.l}</td>
                  <td className="px-3 py-2.5">{row.n}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11.5px] italic text-brand-muted">
          Comps are fictional and illustrative; live IRR uses verified transaction data from the CapitalDart database.
        </p>
      </Section>

      <Section id="profile" n="10" label="Suggested Investor Profile" title="Suggested Investor Profile">
        <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {[
            ["Sector preference", "Healthcare, biotech, gene/cell therapy, therapeutics platforms"],
            ["Stage", "Growth, late-stage venture, crossover"],
            ["Check size", "$8M-$25M lead capacity; smaller follow-on welcome"],
            ["Geography", "United States; selective Western Europe"],
            ["Behavioral fit", "Active board engagement; comfort with clinical-stage risk; demonstrated CMC sophistication"],
            ["Avoid", "Generalist growth funds without healthcare specialist on the team"],
          ].map(([k, v]) => (
            <div key={k} className="rounded-md border border-brand-line bg-brand-paper-2 p-3">
              <dt className="font-mono text-[14px] uppercase tracking-[0.1em] text-brand-muted">{k}</dt>
              <dd className="mt-1 text-[12.5px] text-brand-ink-2">{v}</dd>
            </div>
          ))}
        </dl>
      </Section>

      {/* Footer CTA */}
      <footer className="border-t border-brand-line px-[22px] py-9 lg:px-10 lg:py-12">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-serif text-[16px] text-brand-ink">Ready to run yours?</div>
            <p className="mt-0.5 text-[12.5px] text-brand-muted">
              Drop a deck and a deal description. IRR delivered in about 10 minutes.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild variant="outline">
              <a href="/sample-report.pdf" download>
                Download PDF
              </a>
            </Button>
            <Button asChild>
              <Link to="/auth" search={{ mode: "signup" }}>
                Start a deal <span aria-hidden>→</span>
              </Link>
            </Button>
          </div>
        </div>
        <p className="mt-6 text-[14px] italic text-brand-muted">
          This readiness score is based on the pitch materials and deal
          information provided. It does not include a review of the company's
          financial model, and figures or projections were not independently
          verified. Sample report. Subject and content are fictional and illustrative
          only. Generated by CapitalDart to demonstrate IRR structure and tone.
        </p>
      </footer>
    </>
  );
}